import { createSlice } from '@reduxjs/toolkit';
import { activityInitialState, activityReducers } from '../_shared/activityState';

// One propagation entry per credential: the server unifies every vendor into one row.
export const propagationKey = (credentialID) => String(credentialID);

// How long an accepted Retry's placeholder outranks a settled or missing snapshot row.
const RETRY_PLACEHOLDER_GRACE_MS = 3000;

const initialState = {
	activity: { ...activityInitialState },
	// Scoped: data[orgID][siteID] = { data: [], loading: false }
	data: {},
	error: null,
	// Credentials-table only: lastUsed[orgID][siteID] = { byID: { [credentialID]: iso | null }, loading, ts }.
	// Kept apart from `data` so the shared list stays flag-free and a plain reload can't drop it.
	lastUsed: {},

	// Per-credential loading map: { [credentialID]: 'update' | 'assign' | 'unassign' | 'associate' | 'delete' }
	loadingCredentialIDs: {},

	// GET /credentials/propagation snapshot by credentialID, replaced whole on each refetch.
	credentialPropagation: {},
	// credentialID → { name, credentialTypeID } captured on delete; the server nulls both once the row is gone.
	deletedCredentialMeta: {},

	// Persistent propagation status from DB (pending/failed integrations)
	propagationStatus: [],
	loadingPropagationStatus: false,
	// "orgID:siteID" of the last hydrate; a scope change also drops pending Retry placeholders.
	propagationScope: null,

	// Add Guest

	// Bulk Upload States
	loadingCreateCredential: false,
	loadingBulkCredential: false,
	bulkCredentialError: null,

	// ── Cloud Keys (visitor credentials, Cloud Key WS1) ──────────────────────
	// Loaded from the dedicated /cloud-keys endpoint (richer than generic
	// /credentials rows: recipient, status, doors, masked token, unlock count).
	// Scoped like `data`: cloudKeys[orgID][siteID] = { data: [], loading: false }.
	cloudKeys: {},
	// Issue (provision/send) in-flight + the last one-time URL result (shown once).
	loadingIssueCloudKey: false,
	issuedCloudKey: null,
	// Per-key revoke spinner: { [credentialID]: true }
	loadingRevokeCloudKeyIDs: {},
	// Per-key resend/regenerate spinner: { [credentialID]: true }
	loadingResendCloudKeyIDs: {},
	// The freshly-minted link from the last resend/regenerate (shown once for copy).
	resendResult: null,
	// One key with its doors, for the credential drawer: { [credentialID]: { data, loading } }
	cloudKeyDetail: {},
	// Org emergency kill switch — when engaged the public boundary denies ALL unlocks.
	cloudKeyLockdown: {
		isLockedDown: false,
		loaded: false,
		loading: false,
		saving: false,
		lockedAt: null,
		reason: null
	}
};

// Snapshots name + type of the credentials being deleted from any loaded scope.
const rememberDeletedCredentials = (state, credentialIDs) => {
	const pending = new Set(credentialIDs.map(Number));
	for (const bySite of Object.values(state.data)) {
		for (const bucket of Object.values(bySite)) {
			for (const c of bucket.data || []) {
				if (!pending.has(c.credentialID)) continue;
				state.deletedCredentialMeta[c.credentialID] = {
					name: c.name || c.uniqueIdentifier || null,
					credentialTypeID: c.credentialTypeID ?? null
				};
				pending.delete(c.credentialID);
			}
		}
	}
};

const ensureBucket = (state, section, orgID, siteID) => {
	const key = siteID || 'all';
	if (!state[section][orgID]) state[section][orgID] = {};
	if (!state[section][orgID][key]) state[section][orgID][key] = { data: [], loading: false };
	return key;
};

export const credentialsSlice = createSlice({
	name: 'credentials',
	initialState,
	reducers: {
		...activityReducers,
		// LOAD (shows loading spinner — used for initial load + site switch)
		loadCredentials: () => {},
		loadCredentialsStart(state, action) {
			const { orgID, siteID } = action.payload;
			const key = ensureBucket(state, 'data', orgID, siteID);
			state.data[orgID][key].loading = true;
			state.error = null;
		},
		loadCredentialsSuccess(state, action) {
			const { orgID, siteID, data } = action.payload;
			const key = ensureBucket(state, 'data', orgID, siteID);
			state.data[orgID][key] = { data, loading: false, loaded: true };
		},
		loadCredentialsError(state, action) {
			const { orgID, siteID, error } = action.payload;
			const key = siteID || 'all';
			if (state.data[orgID]?.[key]) state.data[orgID][key].loading = false;
			state.error = error;
		},

		// LAST USED (GET /credentials?includeLastUsed=true, Credentials table only)
		loadCredentialsLastUsed: () => {},
		loadCredentialsLastUsedStart(state, action) {
			const { orgID, siteID } = action.payload;
			const key = siteID || 'all';
			state.lastUsed[orgID] ??= {};
			state.lastUsed[orgID][key] = { byID: {}, ts: null, ...state.lastUsed[orgID][key], loading: true };
		},
		loadCredentialsLastUsedSuccess(state, action) {
			const { orgID, siteID, byID, ts } = action.payload;
			state.lastUsed[orgID][siteID || 'all'] = { byID, loading: false, ts };
		},
		loadCredentialsLastUsedError(state, action) {
			const { orgID, siteID } = action.payload;
			const bucket = state.lastUsed[orgID]?.[siteID || 'all'];
			if (bucket) bucket.loading = false;
		},

		// REFRESH (silent — replaces data without loading flash, used after CRUD operations)
		refreshCredentials: () => {},
		refreshCredentialsSuccess(state, action) {
			const { orgID, siteID, data } = action.payload;
			const key = ensureBucket(state, 'data', orgID, siteID);
			state.data[orgID][key].data = data;
		},

		// CREATE CREDENTIAL
		createCredential: (state) => {
			state.loadingCreateCredential = true;
			state.error = null;
		},
		createCredentialSuccess: (state) => {
			state.loadingCreateCredential = false;
		},
		createCredentialError: (state, action) => {
			state.loadingCreateCredential = false;
			state.error = action.payload;
		},

		// UPDATE CREDENTIAL
		updateCredential: (state, action) => {
			const { credentialID } = action.payload;
			state.loadingCredentialIDs[credentialID] = 'update';
			state.error = null;
		},
		updateCredentialSuccess: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
		},
		updateCredentialError: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.error = action.payload.error;
		},

		// DELETE CREDENTIAL
		// Delete — the modal closes optimistically on dispatch; per-ID loadingCredentialIDs
		// drives the row spinner and the Credential Sync indicator, so no success flag is needed.
		deleteCredential: (state, action) => {
			const { credentialID } = action.payload;
			state.loadingCredentialIDs[credentialID] = 'delete';
			rememberDeletedCredentials(state, [credentialID]);
			state.error = null;
		},
		deleteCredentialSuccess: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
		},
		deleteCredentialError: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.error = action.payload.error;
		},

		// DELETE CREDENTIALS (bulk) — one action carries every ID so the saga can
		// fan the calls out in parallel (per-ID dispatches would self-cancel under takeLatest)
		deleteCredentials: (state, action) => {
			const { credentialIDs } = action.payload;
			credentialIDs.forEach((id) => {
				state.loadingCredentialIDs[id] = 'delete';
			});
			rememberDeletedCredentials(state, credentialIDs);
			state.error = null;
		},
		deleteCredentialsSuccess: (state, action) => {
			const { credentialIDs } = action.payload;
			credentialIDs.forEach((id) => {
				delete state.loadingCredentialIDs[id];
			});
		},
		deleteCredentialsError: (state, action) => {
			const { credentialIDs, error } = action.payload;
			credentialIDs.forEach((id) => {
				delete state.loadingCredentialIDs[id];
			});
			state.error = error;
		},

		// ASSIGN CREDENTIAL
		assignCredential: (state, action) => {
			const { credentialID } = action.payload;
			state.loadingCredentialIDs[credentialID] = 'assign';
			state.error = null;
		},
		assignCredentialSuccess: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
		},
		assignCredentialError: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.error = action.payload.error;
		},

		// UNASSIGN CREDENTIAL
		unassignCredential: (state, action) => {
			const { credentialID } = action.payload;
			state.loadingCredentialIDs[credentialID] = 'unassign';
			state.error = null;
		},
		unassignCredentialSuccess: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
		},
		unassignCredentialError: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.error = action.payload.error;
		},

		// ASSOCIATE CARD
		associateCard: (state, action) => {
			const { credentialID } = action.payload;
			state.loadingCredentialIDs[credentialID] = 'associate';
			state.error = null;
		},
		associateCardSuccess: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
		},
		associateCardError: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.error = action.payload.error;
		},

		// UPDATE CREDENTIAL SITES
		updateCredentialSites: (state) => {
			state.error = null;
		},
		updateCredentialSitesSuccess: () => {},
		updateCredentialSitesError: (state, action) => {
			state.error = action.payload;
		},

		// BATCH IR CARDS
		createCardsBatchIR: (state) => {
			state.loadingBulkCredential = true;
			state.bulkCredentialError = null;
		},
		createCardsBatchIRSuccess: (state) => {
			state.loadingBulkCredential = false;
		},
		createCardsBatchIRError: (state, action) => {
			state.loadingBulkCredential = false;
			state.bulkCredentialError = action.payload;
		},

		// BATCH CONTROL CARDS
		createCardsBatch: (state) => {
			state.loadingBulkCredential = true;
			state.bulkCredentialError = null;
		},
		createCardsBatchSuccess: (state) => {
			state.loadingBulkCredential = false;
		},
		createCardsBatchError: (state, action) => {
			state.loadingBulkCredential = false;
			state.bulkCredentialError = action.payload;
		},

		// BATCH CARD INVENTORY (Zezamii card)
		createCardsBatchInventory: (state) => {
			state.loadingBulkCredential = true;
			state.bulkCredentialError = null;
		},
		createCardsBatchInventorySuccess: (state) => {
			state.loadingBulkCredential = false;
		},
		createCardsBatchInventoryError: (state, action) => {
			state.loadingBulkCredential = false;
			state.bulkCredentialError = action.payload;
		},

		// CREDENTIAL PROPAGATION — snapshot-driven; the only local writes are the Retry placeholder + rollback.

		// RETRY PROPAGATION — optimistic placeholder so the indicator reacts before the re-drive refetches.
		retryCredentialPropagation: (state, action) => {
			const { credentialID, deviceIDs } = action.payload;
			if (!credentialID) return;
			const key = propagationKey(credentialID);
			const allDeviceIDs = deviceIDs || [];
			const existing = state.credentialPropagation[key];
			if (existing) {
				// Keep the entry's type/name/total (a retry doesn't change the lock set); reset only the counts.
				const deviceSet = new Set(allDeviceIDs);
				const keptResults =
					allDeviceIDs.length > 0
						? (existing.results || []).filter((r) => r.status === 'registered' && !deviceSet.has(r.deviceID))
						: [];
				state.credentialPropagation[key] = {
					...existing,
					credentialID,
					current: keptResults.length,
					total: Math.max(existing.total || 0, keptResults.length + allDeviceIDs.length),
					succeeded: keptResults.length,
					results: keptResults,
					complete: false,
					queued: false,
					failed: 0,
					failedDeviceIDs: [],
					// The old DB write time would read as stalled; the next snapshot brings the real one.
					_touchedAt: null,
					_retry: true,
					_retryAcceptedAt: null
				};
			} else {
				state.credentialPropagation[key] = {
					credentialID,
					current: 0,
					total: allDeviceIDs.length,
					succeeded: 0,
					results: [],
					complete: false,
					failed: 0,
					failedDeviceIDs: [],
					_retry: true,
					_retryAcceptedAt: null
				};
			}
			// Remove retried items from the Pending tab (propagationStatus)
			if (allDeviceIDs.length > 0) {
				const deviceSet = new Set(allDeviceIDs);
				state.propagationStatus = state.propagationStatus.filter(
					(item) => !(item.credentialID === credentialID && deviceSet.has(item.deviceID))
				);
			} else {
				state.propagationStatus = state.propagationStatus.filter((item) => item.credentialID !== credentialID);
			}
		},

		// RETRY ACCEPTED — the server re-queued the rows; the grace window starts now.
		credentialPropagationRetryAccepted: (state, action) => {
			const entry = state.credentialPropagation[propagationKey(action.payload?.credentialID)];
			if (entry?._retry) entry._retryAcceptedAt = Date.now();
		},

		// RETRY FAILED — the request never started a job: drop a still-_retry placeholder so it can't spin
		// forever. The saga follows with fetchPropagation() to restore the Failed/Attention tabs.
		credentialPropagationRetryFailed: (state, action) => {
			const { credentialID } = action.payload || {};
			if (!credentialID) return;
			const key = propagationKey(credentialID);
			const entry = state.credentialPropagation[key];
			if (entry && entry._retry && !entry.complete) {
				delete state.credentialPropagation[key];
			}
		},

		// FETCH PROPAGATION (single action — triggers GET /credentials/propagation)
		fetchPropagation: () => {},
		hydrateCredentialPropagation: (state, action) => {
			const { queued = [], active = [], completed = [], scope = null } = action.payload;

			// The snapshot is the single source of truth: fixed totals and server counts, stored as-is.
			// A job that finished clean is absent from it, so "gone = done" for assign and unassign alike.
			const build = (entry, extra) => ({
				...entry,
				type: entry.type || 'assign',
				results: entry.results || [],
				// Real DB last-write (epoch ms) for stalled detection — not a client clock.
				_touchedAt: entry.touchedAt || null,
				...extra
			});

			const incoming = {};
			for (const entry of queued) incoming[propagationKey(entry.credentialID)] = build(entry, { queued: true });
			for (const entry of active) incoming[propagationKey(entry.credentialID)] = build(entry, { queued: false });
			for (const entry of completed) {
				incoming[propagationKey(entry.credentialID)] = build(entry, { queued: false, complete: true });
			}

			// Keep only Retry placeholders the snapshot has not caught up to; drop them on a scope change.
			const scopeChanged = scope !== null && scope !== state.propagationScope;
			if (scope !== null) state.propagationScope = scope;

			const preserved = {};
			if (!scopeChanged) {
				for (const [key, live] of Object.entries(state.credentialPropagation)) {
					if (!live._retry || live.complete) continue;
					if (incoming[key] && !incoming[key].complete) continue;
					// Held while the POST runs and for the grace after; past that, missing means it finished clean.
					const acceptedAt = live._retryAcceptedAt;
					if (acceptedAt == null || Date.now() - acceptedAt < RETRY_PLACEHOLDER_GRACE_MS)
						preserved[key] = live;
				}
			}
			state.credentialPropagation = { ...incoming, ...preserved };
		},

		// PROPAGATION STATUS (populated by fetchPropagation response)
		fetchPropagationStatusSuccess: (state, action) => {
			state.propagationStatus = action.payload;
			state.loadingPropagationStatus = false;
		},
		fetchPropagationStatusError: (state) => {
			state.loadingPropagationStatus = false;
		},

		// ── CLOUD KEYS — list (loading spinner: initial load + site switch) ──────
		loadCloudKeys: () => {},
		loadCloudKeysStart(state, action) {
			const { orgID, siteID } = action.payload;
			const key = ensureBucket(state, 'cloudKeys', orgID, siteID);
			state.cloudKeys[orgID][key].loading = true;
			state.error = null;
		},
		loadCloudKeysSuccess(state, action) {
			const { orgID, siteID, data } = action.payload;
			const key = ensureBucket(state, 'cloudKeys', orgID, siteID);
			state.cloudKeys[orgID][key] = { data, loading: false };
		},
		loadCloudKeysError(state, action) {
			const { orgID, siteID, error } = action.payload;
			const key = siteID || 'all';
			if (state.cloudKeys[orgID]?.[key]) state.cloudKeys[orgID][key].loading = false;
			state.error = error;
		},

		// CLOUD KEYS — silent refresh (after issue/revoke, no loading flash)
		refreshCloudKeys: () => {},
		refreshCloudKeysSuccess(state, action) {
			const { orgID, siteID, data } = action.payload;
			const key = ensureBucket(state, 'cloudKeys', orgID, siteID);
			state.cloudKeys[orgID][key].data = data;
		},

		// CLOUD KEYS — issue (provision returns URL only; send also emails it)
		provisionCloudKey: (state) => {
			state.loadingIssueCloudKey = true;
			state.issuedCloudKey = null;
			state.error = null;
		},
		sendCloudKey: (state) => {
			state.loadingIssueCloudKey = true;
			state.issuedCloudKey = null;
			state.error = null;
		},
		issueCloudKeySuccess: (state, action) => {
			state.loadingIssueCloudKey = false;
			state.issuedCloudKey = action.payload; // { credentialID, url, keySlug, devices, recipient, deliveryStatus? }
		},
		issueCloudKeyError: (state, action) => {
			state.loadingIssueCloudKey = false;
			// Email-failure semantics: the key WAS created (502 carries credentialID) —
			// surface it so the UI can offer resend and never imply the key was lost.
			state.issuedCloudKey = action.payload?.credentialID
				? { credentialID: action.payload.credentialID, deliveryStatus: 'FAILED' }
				: null;
			state.error = action.payload?.error || action.payload;
		},
		clearIssuedCloudKey: (state) => {
			state.issuedCloudKey = null;
		},

		// CLOUD KEYS — revoke a single key
		revokeCloudKey: (state, action) => {
			const { credentialID } = action.payload;
			state.loadingRevokeCloudKeyIDs[credentialID] = true;
			state.error = null;
		},
		revokeCloudKeySuccess: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingRevokeCloudKeyIDs[credentialID];
		},
		revokeCloudKeyError: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingRevokeCloudKeyIDs[credentialID];
			state.error = action.payload.error;
		},

		// CLOUD KEYS — resend / regenerate (re-mint server-side; previous link stops working)
		resendCloudKey: (state, action) => {
			const { credentialID } = action.payload;
			state.loadingResendCloudKeyIDs[credentialID] = true;
			state.resendResult = null;
			state.error = null;
		},
		resendCloudKeySuccess: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingResendCloudKeyIDs[credentialID];
			state.resendResult = action.payload; // { credentialID, url, deliveryStatus }
		},
		resendCloudKeyError: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingResendCloudKeyIDs[credentialID];
			state.error = action.payload.error;
		},
		clearResendResult: (state) => {
			state.resendResult = null;
		},

		// CLOUD KEYS — one key with its doors (GET /cloud-keys/:credentialID)
		loadCloudKeyDetail: (state, action) => {
			const { credentialID } = action.payload;
			state.cloudKeyDetail[credentialID] = { data: state.cloudKeyDetail[credentialID]?.data ?? null, loading: true };
		},
		loadCloudKeyDetailSuccess: (state, action) => {
			const { credentialID, data } = action.payload;
			state.cloudKeyDetail[credentialID] = { data, loading: false };
		},
		loadCloudKeyDetailError: (state, action) => {
			const { credentialID } = action.payload;
			if (state.cloudKeyDetail[credentialID]) state.cloudKeyDetail[credentialID].loading = false;
		},

		// ── CLOUD KEYS — org emergency lockdown (kill switch) ────────────────────
		loadCloudKeyLockdown: (state) => {
			state.cloudKeyLockdown.loading = true;
		},
		loadCloudKeyLockdownSuccess: (state, action) => {
			const d = action.payload || {};
			state.cloudKeyLockdown = {
				isLockedDown: !!d.isLockedDown,
				loaded: true,
				loading: false,
				saving: false,
				lockedAt: d.lockedAt || null,
				reason: d.reason || null
			};
		},
		loadCloudKeyLockdownError: (state) => {
			state.cloudKeyLockdown.loading = false;
			state.cloudKeyLockdown.loaded = true;
		},
		setCloudKeyLockdown: (state) => {
			state.cloudKeyLockdown.saving = true;
			state.error = null;
		},
		setCloudKeyLockdownSuccess: (state, action) => {
			const d = action.payload || {};
			state.cloudKeyLockdown = {
				isLockedDown: !!d.isLockedDown,
				loaded: true,
				loading: false,
				saving: false,
				lockedAt: d.lockedAt || null,
				reason: d.reason || null
			};
		},
		setCloudKeyLockdownError: (state) => {
			state.cloudKeyLockdown.saving = false;
		},

		// Loading helpers
		updateCredentialValidityLoading: (state) => {
			state.loadingCredentialIDs[state._pendingCredentialID] = 'update';
		},
		updateCredentialPersonLoading: (state) => {
			state.loadingCredentialIDs[state._pendingCredentialID] = 'update';
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase('auth/logout', () => initialState)
			.addCase('auth/switchProfile', (state) => {
				// Clear propagation state on org switch — new org will rehydrate via fetchPropagation
				state.credentialPropagation = {};
				state.propagationStatus = [];
				state.deletedCredentialMeta = {};
				// Drop any half-finished cloud-key issue state from the previous org.
				state.issuedCloudKey = null;
				state.loadingIssueCloudKey = false;
				state.cloudKeyDetail = {};
				// Lockdown is org-specific — reset so the new org re-fetches authoritative state.
				state.cloudKeyLockdown = {
					isLockedDown: false,
					loaded: false,
					loading: false,
					saving: false,
					lockedAt: null,
					reason: null
				};
			});
	}
});

export const credentialsActions = credentialsSlice.actions;
export default credentialsSlice.reducer;
